<template>
<div class="container bg-white h-100 w-100">
    <div class="row h-100">

    <!-- Cargando -->
    <div v-if="isLoading" class="col-12 text-center my-4">
        <b-spinner label="Loading" />
    </div>

    <template v-if="!isLoading">

        <div v-if="loadError" class="col-12 text-center" style="padding-top: 20%;">
        <h4>{{ $t(`plane.${loadError}`) }}</h4>
        </div>

        <!-- Sin planos -->
        <div v-else-if="planes.length === 0" class="col-12 text-center" style="padding-top: 20%;">
        <h4>{{ $t("plane.no_floor_plant") }}</h4>
        </div>

        <!-- Con planos -->
        <div v-else class="col-12 p-1">
        <div class="border rounded p-1" style="background-color: #f2f2f2;">

            <!-- Tabs -->
            <ul class="nav nav-tabs">
            <li
                class="nav-item"
                v-for="(plane, index) in planes"
                :key="plane.id"
            >
                <button
                class="nav-link"
                :class="{ active: activeTab === index }"
                :title="plane.name.length > 15 ? plane.name : ''"
                @click="activeTab = index"
                >
                <span class="tab-name">{{ plane.name }}</span>
                </button>
            </li>
            </ul>

            <!-- Contenido tabs -->
            <div class="tab-content border rounded bg-white p-1">
            <div
                v-for="(plane, index) in planes"
                :key="plane.id"
                v-show="activeTab === index"
            >
                <div
                class="planeViewport"
                @wheel="onWheel($event, index)"
                @pointerdown="startPan($event, index)"
                @pointermove="movePan($event, index)"
                @pointerup="endPan(index)"
                @pointerleave="endPan(index)"
                @dragover.prevent
                @drop.prevent
                >

                <!-- Overlay para cerrar popover al click fuera -->
                <div
                    v-if="activeInfoDeviceId"
                    class="plane-overlay"
                    @click="activeInfoDeviceId = null"
                />

                    <div class="planeScene" :style="sceneTransformForIndex(index)">

                        <div v-if="!loadedImages[plane.id]" class="plane-skeleton">
                            <div class="plane-skeleton__shimmer" />
                        </div>

                        <img
                        :src="plane.path"
                        class="planeImage"
                        draggable="false"
                        @load="onImageLoad(plane.id)"
                        :style="{ opacity: loadedImages[plane.id] ? 1 : 0, transition: 'opacity 0.3s ease' }"
                        />

                        <Device
                            v-for="device in plane.devices"
                            :key="device.id"
                            :device="device"
                            :name="device.name"
                            :position="device.position"
                            :scale="getViewportState(index).scale"
                            :isCamera="!!device.position.cone.visible"
                            :activeInfoDeviceId="activeInfoDeviceId"
                            :showAlarm="device.showAlarm"
                            :alarmColor="device.alarmColor"
                            :showAlarmActions="device.can_update_alarm"
                            @toggle-info="activeInfoDeviceId = $event"
                            @actuator-info="onActuatorInfo"
                            @interphone-action="onInterphoneAction"
                            @alarm-action="refreshAlarms"
                            @update-alarm-style="onUpdateAlarmStyle"
                        />

                    </div>

                </div>
            </div>
            </div>

        </div>
        </div>

    </template>

    </div>

</div>
</template>

<script>
import router from '@/router'
import { ref, onMounted, watch, computed, set } from "@vue/composition-api"
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig"
import layoutMixin from "@/mixins/layoutMixin"
import { usePlaneSocket } from "./composables/usePlaneSocket"
import store from "@/store"
import Device from "./Device.vue"
import PlaneDevice, { triggerPanelRefresh, updateDeviceInCache } from './Device.vue'

export default {
components: { Device },
mixins: [layoutMixin],
props: {
  data: {
    type: Object,
    default: null
  }
},
setup(props) {

    const hasExplicitUnitId = computed(() => {
        const slotData = props.data?.data ?? props.data

        return !!slotData && Object.prototype.hasOwnProperty.call(slotData, 'unit_id')
    })
    const id = computed(() => {
        if (hasExplicitUnitId.value) {
            return props.data?.data?.unit_id ?? props.data?.unit_id ?? null
        }

        return router.currentRoute.params.id
    })
    const planes    = ref([])
    const isLoading = ref(true)
    const loadError = ref(null)
    const loadedImages = ref({})
    const activeTab = ref(0)
    const activeInfoDeviceId = ref(null)

    const onImageLoad = (planeId) => {
        loadedImages.value = { ...loadedImages.value, [planeId]: true }
    }

    // ─── Zoom y pan independiente por plano ───────────────────────────────────
    const viewportStates = ref({})

    const getViewportState = (index) => {
    if (!viewportStates.value[index]) {
        set(viewportStates.value, index, {
        scale:      1,
        translateX: 0,
        translateY: 0,
        isPanning:  false,
        startX:     0,
        startY:     0,
        })
    }
    return viewportStates.value[index]
    }

    const sceneTransformForIndex = (index) => {
    const s = getViewportState(index)
    return {
        transform: `translate(${s.translateX}px, ${s.translateY}px) scale(${s.scale})`
    }
    }

    const onWheel = (e, index) => {
    e.preventDefault()
    const s           = getViewportState(index)
    const zoomIntensity = 0.001
    const delta       = -e.deltaY
    const newScale    = Math.min(Math.max(0.3, s.scale + delta * zoomIntensity), 4)
    const rect        = e.currentTarget.getBoundingClientRect()
    const mouseX      = e.clientX - rect.left
    const mouseY      = e.clientY - rect.top
    const scaleFactor = newScale / s.scale

    s.translateX = mouseX - scaleFactor * (mouseX - s.translateX)
    s.translateY = mouseY - scaleFactor * (mouseY - s.translateY)
    s.scale      = newScale
    }

    const startPan = (e, index) => {
        activeInfoDeviceId.value = null
    const s    = getViewportState(index)
    s.isPanning = true
    s.startX   = e.clientX - s.translateX
    s.startY   = e.clientY - s.translateY
    }

    const movePan = (e, index) => {
    const s = getViewportState(index)
    if (!s.isPanning) return
    s.translateX = e.clientX - s.startX
    s.translateY = e.clientY - s.startY
    }

    const endPan = (index) => {
    getViewportState(index).isPanning = false
    }

    // ─── WebSocket por plano ────────────────────────────────────────────────────── 
    const { connect: connectPlanes } = usePlaneSocket(
        // 1. device.updated normal
        (event, plane) => {
            const proxyId    = event.data?.id_camera_proxy
            const deviceData = event.data?.data
            if (!proxyId || !deviceData) return

            const ruleColor  = deviceData.rule?.color || null
            const ruleIcon   = deviceData.rule?.icon  || null
            const alarmColor = ruleColor ?? deviceData.color ?? '#6e6b7b'
            const alarmIcon  = ruleIcon ?? null

            planes.value.forEach(p => {
                const device = p.devices.find(d => d.id_camera_proxy === proxyId)
                if (device) {
                    device.showAlarm  = true
                    device.alarmColor = alarmColor
                    device.alarmIcon  = alarmIcon
                    device.source_id  = deviceData.source_id ?? device.source_id
                }
            })
        },
        // 2. dispatched_alarm / canceled_alarm
        async (event) => {
            const eventType = event.event_type
            const typeId    = event.data?.type_id
            if (!typeId) return

            const alarmId = `alarm-${typeId}`
            planes.value.forEach(p => {
                if (event.id_floor_plant && event.id_floor_plant !== p.id) return
                p.devices.forEach(device => {
                    if (device.id_generic === alarmId) {
                        device.active_alarm = eventType === 'dispatched_alarm'
                    }
                })
            })
            await refreshAlarms()
        },
        // 3. dmp_panel_update / dmp_panel_device_alarm
        async (event) => {
            const deviceData = event.data?.data
            const proxyId    = event.data?.id_camera_proxy
            const eventType  = event.event_type

            if (proxyId && deviceData) {
                const tag        = deviceData.tag  || null
                const rule       = deviceData.rule || null
                const alarmColor = (tag && tag.color) || (rule && rule.color) || deviceData.color || '#cf1111'
                const alarmIcon  = (tag && tag.icon)  || (rule && rule.icon)  || null

                planes.value.forEach(p => {
                    if (event.id_floor_plant && event.id_floor_plant !== p.id) return
                    p.devices.forEach(device => {
                        if (device.id_camera_proxy !== proxyId) return

                        // Solo actualizar estado y showAlarm — alarmStyle calculará el resto
                        device.status    = deviceData.status    || device.status
                        device.source_id = deviceData.source_id || device.source_id

                        if (eventType === 'dmp_panel_device_alarm') {
                            device.showAlarm = true
                        } else if (eventType === 'dmp_panel_update') {
                            const status = (deviceData.status || '').toUpperCase()
                            if (['ALARMED', 'BURGLARY', 'ALARM', 'FAULT'].includes(status)) {
                                device.showAlarm = true
                            } else {
                                device.showAlarm = false
                            }
                        }
                    })
                })
            }

            // Recargar info del panel UNA sola vez
            const panelProxyId = event.data?.panel_proxy_id || proxyId
            if (panelProxyId) {
                delete panelInfoCache[panelProxyId]
                const raw = await loadPanelInfo(panelProxyId)
                if (raw) {
                    planes.value.forEach(plane => {
                        if (event.id_floor_plant && event.id_floor_plant !== plane.id) return
                        plane.devices.forEach(device => {
                            if (String(device.panel_proxy_id) !== String(panelProxyId) &&
                                String(device.id_camera_proxy) !== String(panelProxyId)) return
                            const latestAlarm = findLatestAlarmInRaw(raw, device.id_camera_proxy)
                            if (latestAlarm) {
                                set(device, 'latest_alarm', latestAlarm)
                                const t = latestAlarm.tag  && latestAlarm.tag.icon  ? latestAlarm.tag  : null
                                const r = latestAlarm.rule && latestAlarm.rule.icon ? latestAlarm.rule : null
                                set(device, 'alarmIcon',  (t && t.icon)       || (r && r.icon)  || device.alarmIcon  || null)
                                set(device, 'alarmColor', (t && t.icon_color) || (r && r.color) || latestAlarm.color || '#cf1111')
                            }
                        })
                    })
                }
            }
        }
    )
    // ─── Carga de planos ──────────────────────────────────────────────────────
    const loadPlanes = async () => {
    isLoading.value = true
    loadError.value = null

    if (!id.value) {
        loadError.value = 'floor_plant_context_missing'
        isLoading.value = false
        return
    }

    let floorRes
    try {
        floorRes = await store.dispatch("unit/getFloorPlantAll", id.value)
    } catch (err) {
        loadError.value = err?.response?.status === 403
            ? 'no_floor_plant_permission'
            : 'floor_plant_load_error'
        console.error('Error al cargar planos:', err)
        isLoading.value = false
        return
    }

    let relationsRes = { data: {} }
    try {
        relationsRes = await store.dispatch("unit/getWithRelations", {
            id: id.value,
            params: { withCameras: false, withDevices: true, view: 'grid' }
        })
    } catch (err) {
        console.error('Error al cargar relaciones de la instalación:', err)
    }

    try {

        // Mapear alarmas
        const alarmsRaw = relationsRes.data?.devices?.[0]?.alarms ?? []
        const alarmsMap = {}
        alarmsRaw.forEach(alarm => {
        alarmsMap[alarm.id] = {
            name:       alarm.data?.name       ?? alarm.id,
            color:      alarm.data?.color      ?? '#6c757d',
            text_color: alarm.data?.text_color ?? '#ffffff',
            icon:       alarm.data?.icon       ?? null,
            color_icon: alarm.data?.color_icon ?? '#ffffff',
            acronym:    alarm.data?.acronym    ?? '?',
            active_alarm:     alarm.data?.active_alarm ?? false,
            can_update_alarm: alarm.can_update_alarm   ?? false,
            dispatch_url: alarm.actions?.dispatch_alarm ?? null,
            cancel_url:   alarm.actions?.cancel_alarm   ?? null,
        }
        })

        // Mapear dmp_panel_status para lookup rápido
        const dmpStatusRaw = relationsRes.data?.devices?.[0]?.dmp_panel_status ?? []
        const dmpStatusMap = {}
        dmpStatusRaw.forEach(status => {
        dmpStatusMap[status.id] = {
            name:       status.data?.name      ?? status.id,
            icon:       status.data?.icon      ?? null,
            color:      status.data?.color     ?? null,
            color_icon: status.data?.color_icon ?? null,
            text_color: status.data?.text_color ?? null,
            value:      status.data?.value     ?? null,
            device_type:  'dmp_panel_status',
            camera_type:  'dmp_panel_status',
        }
        })

        // Mapear dmp_panel_actions
        const dmpActionsRaw = relationsRes.data?.devices?.[0]?.dmp_panel_actions ?? []
        const dmpActionsMap = {}
        dmpActionsRaw.forEach(action => {
        dmpActionsMap[action.id] = {
            name:       action.data?.name       ?? action.id,
            icon:       action.data?.icon       ?? null,
            color:      action.data?.color      ?? null,
            color_icon: action.data?.color_icon ?? null,
            text_color: action.data?.text_color ?? null,
            actions:    action.actions          ?? {},
            device_type: 'dmp_panel_action',
            camera_type: 'dmp_panel_action',
        }
        })

        const sortedPlanes = (floorRes.data || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

        planes.value = sortedPlanes.map((plane, index) => {
        const mappedDevices = (plane.devices || []).map(device => {
            const isGenericAlarm  = device.id_generic && String(device.id_generic).startsWith('alarm-')
            const isDmpStatus     = device.id_generic && String(device.id_generic).startsWith('dmp-status-')
            const isDmpAction    = device.id_generic && String(device.id_generic).startsWith('dmp-action-')   
            const alarmData       = isGenericAlarm ? (alarmsMap[device.id_generic] ?? null) : null
            const dmpStatusData   = isDmpStatus    ? (dmpStatusMap[device.id_generic] ?? null) : null 
            const dmpActionData  = isDmpAction    ? (dmpActionsMap[device.id_generic] ?? null) : null 

            let panelProxyId = device.device?.panel_proxy_id || null
            if (!panelProxyId && device.device?.device_panel_type === 'area') {
            const bridge = plane.devices.find(d => d.device?.device_type === 'alarm_bridge')
            panelProxyId = bridge?.id_camera_proxy ?? null
            }

            const rawPanelType = device.device?.device_panel_type ?? null
            const cameraType   = device.device?.type ?? null

            const inferPanelType = (panelType, camType) => {
            if (panelType && panelType !== 'panel') return panelType
            if (!camType) return panelType
            const match = camType.match(/alarm_bridge_(.+)/)
            return match ? match[1] : panelType
            }

            const resolvedPanelType = inferPanelType(rawPanelType, cameraType)

            return {
            id:              device.id_generic ?? device.id_camera_proxy,
            name:            dmpActionData?.name ?? dmpStatusData?.name ?? alarmData?.name ?? (device.device ? device.device.name : `Device ${device.id_camera_proxy}`),
            id_camera_proxy: device.id_camera_proxy,
            floor_plant_id:  plane.id,
            id_generic:      device.id_generic ?? null,
            camera_id:       device.device ? device.device.camera_id : null,
            device_type:     isDmpAction ? 'dmp_panel_action' : (isDmpStatus ? 'dmp_panel_status' : (isGenericAlarm ? 'alarm' : (device.device ? device.device.device_type : null))),
            camera_type:     isDmpAction ? 'dmp_panel_action' : (isDmpStatus ? 'dmp_panel_status' : (isGenericAlarm ? 'alarm' : (device.device ? device.device.type : null))),
            status:          device.device ? device.device.status : null,
            panel_device_type: resolvedPanelType,
            panel_proxy_id:  panelProxyId,
            proxy_type:      device.device ? device.device.device_type : null,
            proxy_camera_type: device.device ? device.device.type : null,
            source_id:       device.device?.related_device?.source_id ?? device.device?.source_id ?? null,
            icon:       dmpActionData?.icon       ?? dmpStatusData?.icon       ?? alarmData?.icon       ?? null,
            color:      dmpActionData?.color      ?? dmpStatusData?.color      ?? alarmData?.color      ?? null,
            color_icon: dmpActionData?.color_icon ?? dmpStatusData?.color_icon ?? alarmData?.color_icon ?? null,
            text_color: dmpActionData?.text_color ?? dmpStatusData?.text_color ?? alarmData?.text_color ?? null,
            acronym:    alarmData?.acronym ?? null,
            value:      dmpStatusData?.value ?? null,
            actions:    dmpActionData?.actions ?? null,
            can_update_alarm: alarmData?.can_update_alarm ?? false,
            dispatch_url: alarmData?.dispatch_url ?? null,
            cancel_url:   alarmData?.cancel_url   ?? null,
            showAlarm:    false,
            active_alarm: alarmData?.active_alarm ?? false,
            alarmColor:   '',
            alarmIcon:    null,
            position: {
                x:      device.x,
                y:      device.y,
                width:  device.width,
                height: device.height,
                degree: device.degree,
                cone: {
                visible:  !!device.cone_visible,
                distance: device.cone_distance ? parseFloat(device.cone_distance) : 80,
                angle:    device.cone_angle    ? parseFloat(device.cone_angle)    : 45,
                },
            }
          }
        })

        set(viewportStates.value, index, {
            scale:      parseFloat(plane.scale)       || 1,
            translateX: parseFloat(plane.translate_x) || 0,
            translateY: parseFloat(plane.translate_y) || 0,
            isPanning:  false,
            startX:     0,
            startY:     0,
        })

        return {
            id:      plane.id,
            name:    plane.name,
            path:    plane.path,
            devices: mappedDevices,
        }
        })

    } catch (err) {
        loadError.value = 'floor_plant_load_error'
        console.error('Error al preparar planos:', err)
    }
    isLoading.value = false
    }

    //Esta funcion la vamos a utilizar para actualizar los datos de las alarmas cada action que ejecutamos
    const refreshAlarms = async () => {
    try {
        const relationsRes = await store.dispatch("unit/getWithRelations", {
        id: id.value,
        params: { withCameras: false, withDevices: true, view: 'grid' }
        })

        const alarmsRaw = relationsRes.data?.devices?.[0]?.alarms ?? []
        const alarmsMap = {}
        alarmsRaw.forEach(alarm => {
        alarmsMap[alarm.id] = {
            name:             alarm.data?.name         ?? alarm.id,
            color:            alarm.data?.color        ?? '#6c757d',
            text_color:       alarm.data?.text_color   ?? '#ffffff',
            icon:             alarm.data?.icon         ?? null,
            color_icon:       alarm.data?.color_icon   ?? '#ffffff',
            acronym:          alarm.data?.acronym      ?? '?',
            active_alarm:     alarm.data?.active_alarm ?? false,
            can_update_alarm: alarm.can_update_alarm   ?? false,
            dispatch_url:     alarm.actions?.dispatch_alarm ?? null,
            cancel_url:       alarm.actions?.cancel_alarm   ?? null,
        }
        })

        // Actualizar los devices de tipo alarm en todos los planos
        planes.value.forEach(plane => {
        plane.devices.forEach(device => {
            if (device.device_type === 'alarm' && device.id_generic) {
            const alarmData = alarmsMap[device.id_generic]
            if (alarmData) {
                device.active_alarm     = alarmData.active_alarm
                device.can_update_alarm = alarmData.can_update_alarm
                device.dispatch_url     = alarmData.dispatch_url
                device.cancel_url       = alarmData.cancel_url
            }
            }
        })
        })
    } catch (err) {
        console.error('Error al refrescar alarmas:', err)
    }
    }

    const onUpdateAlarmStyle = ({ device, alarmColor, alarmIcon, showAlarm }) => {
        planes.value.forEach(p => {
            const d = p.devices.find(dev => dev.id_camera_proxy === device.id_camera_proxy)
            if (d) {
            d.alarmColor = alarmColor
            d.alarmIcon  = alarmIcon
            d.showAlarm  = showAlarm
            }
        })
    }

    // Cache global en index.vue — keyed por panel_proxy_id
    const panelInfoCache = {}

    const loadPanelInfo = async (panelProxyId) => {
    if (!panelProxyId) return null
    
    // Si ya está en cache retornar inmediatamente
    if (panelInfoCache[panelProxyId]) return panelInfoCache[panelProxyId]

    await store.dispatch("alarm_panels/getInfo", {
        device_proxy_id: panelProxyId,
        withDevices:     true,
    })
    const raw = store.getters["alarm_panels/getItems"].devices || {}
    panelInfoCache[panelProxyId] = raw
    return raw
    }

    // Llamar al montar para todos los devices del plano
    const loadAllDeviceInfo = async () => {
    // Agrupar devices por panel_proxy_id para hacer UNA sola request por panel
    const panelProxyIds = new Set()
    planes.value.forEach(plane => {
        plane.devices.forEach(device => {
        if (device.panel_proxy_id) panelProxyIds.add(device.panel_proxy_id)
        else if (device.device_type === 'alarm_bridge') panelProxyIds.add(device.id_camera_proxy)
        })
    })

    // Cargar info de cada panel UNA sola vez
    await Promise.all([...panelProxyIds].map(id => loadPanelInfo(id)))

    // Actualizar cada device con su latest_alarm
    planes.value.forEach(plane => {
        plane.devices.forEach(device => {
            const panelId = device.panel_proxy_id || 
                            (device.device_type === 'alarm_bridge' ? device.id_camera_proxy : null)
            if (!panelId) return

            const raw = panelInfoCache[panelId]
            if (!raw) return

            const latestAlarm = findLatestAlarmInRaw(raw, device.id_camera_proxy)
            if (latestAlarm) {
                set(device, 'latest_alarm', latestAlarm) 
                const t = latestAlarm.tag  && latestAlarm.tag.icon  ? latestAlarm.tag  : null
                const r = latestAlarm.rule && latestAlarm.rule.icon ? latestAlarm.rule : null
                set(device, 'alarmIcon',  (t && t.icon)       || (r && r.icon)  || device.alarmIcon  || null)
                set(device, 'alarmColor', (t && t.icon_color) || (r && r.color) || latestAlarm.color || '#cf1111')
            }
        })
    })
    }

    const findLatestAlarmInRaw = (raw, id_camera_proxy) => {
    if (!raw || !id_camera_proxy) return null
    for (const key in raw) {
        if (key === 'panel') continue
        if (Array.isArray(raw[key])) {
        for (const item of raw[key]) {
            if (String(item.device_proxy_id) === String(id_camera_proxy)) {
            return item.latest_alarm && !Array.isArray(item.latest_alarm) ? item.latest_alarm : null
            }
            if (item.devices && Array.isArray(item.devices)) {
            for (const sub of item.devices) {
                if (String(sub.device_proxy_id) === String(id_camera_proxy)) {
                return sub.latest_alarm && !Array.isArray(sub.latest_alarm) ? sub.latest_alarm : null
                }
            }
            }
        }
        }
    }
    return null
    }

    onMounted(async () => {
        await loadPlanes()
        await loadAllDeviceInfo()
        connectPlanes(planes.value) 
    })

    return {
    id,
    planes,
    isLoading,
    loadError,
    activeTab,
    viewportStates,
    getViewportState,
    sceneTransformForIndex,
    onWheel,
    startPan,
    movePan,
    endPan,
    activeInfoDeviceId,
    loadedImages,
    onImageLoad,
    refreshAlarms,
    onUpdateAlarmStyle,
    }
    
},
methods: {
    onActuatorInfo({ device }) {
        const urlActuators = `${window.__env.VUE_APP_BASE_URL}/v1/services/actuators/info/`
        this.setSlotData(
        SLOT_TYPES.INFO,
        device.name,
        { data: { url: urlActuators + device.id_camera_proxy } },
        SOURCES.ACTUATOR
        )
    },
    onInterphoneAction({ device }) {
        this.setSlotData(
            SLOT_TYPES.INTERPHONE,
            device.id_camera_proxy,
            { data: device }
        )
    }
}

}
</script>

<style lang="scss" scoped>
.planeViewport {
width: 100%;
height: 70vh;
overflow: hidden;
position: relative;
cursor: grab;
}


.planeViewport:active {
cursor: grabbing;
}

.planeScene {
position: absolute;
transform-origin: top left;
overflow: visible;
}

.planeImage {
display: block;
user-select: none;
pointer-events: none;
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────
.nav-tabs {
border-bottom: none;
gap: 4px;
padding: 6px 6px 0 6px;
}

.nav-tabs .nav-item .nav-link {
border: none;
border-radius: 8px 8px 0 0;
padding: 6px 14px;
font-size: 13px;
font-weight: 500;
color: #385d7b;
background: rgba(255, 255, 255, 0.4);
transition: all 0.2s ease;

&:hover {
    background: rgba(255, 255, 255, 0.7);
    color: #495057;
}

&.active {
    background: #385d7b;
    color: white;
    font-weight: 600;
}
}

.tab-name {
display: inline-block;
max-width: 120px;
overflow: hidden;
text-overflow: ellipsis;
white-space: nowrap;
vertical-align: middle;
}

.plane-skeleton {
  position: absolute;
  width: 100%;
  height: 100%;
  min-width: 400px;
  min-height: 300px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.plane-skeleton__shimmer {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0)    0%,
    rgba(255,255,255,0.6)  50%,
    rgba(255,255,255,0)    100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}

.plane-overlay {
  position: fixed;
  inset: 0;
  z-index: 99;
  background: transparent;
  cursor: default;
}
</style>
